import React, { useState } from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import type { Dayjs } from "dayjs";
import { brand } from "../../../../theme/themePrimitives";

export type FilterItem = {
  id: string;
  name: string;
  checked?: boolean;
};

export type FilterGroup = {
  title: string;
  items: FilterItem[];
};

type Props = {
  filtersList: FilterGroup[];
  showFilter: boolean;
  setShowFilter: (v: boolean) => void;
  handleCheckboxChange: (groupIndex: number, itemIndex: number) => void;
  saveFilters: () => void;
  clearFilters: () => void;
  startDateFrom: Dayjs | null;
  startDateTo: Dayjs | null;
  onStartDateFromChange: (date: Dayjs | null) => void;
  onStartDateToChange: (date: Dayjs | null) => void;
  completionDateFrom: Dayjs | null;
  completionDateTo: Dayjs | null;
  onCompletionDateFromChange: (date: Dayjs | null) => void;
  onCompletionDateToChange: (date: Dayjs | null) => void;
};

export const Filter: React.FC<Props> = ({
  filtersList,
  showFilter,
  setShowFilter,
  handleCheckboxChange,
  saveFilters,
  clearFilters,
  startDateFrom,
  startDateTo,
  onStartDateFromChange,
  onStartDateToChange,
  completionDateFrom,
  completionDateTo,
  onCompletionDateFromChange,
  onCompletionDateToChange,
}) => {
  const [openStartFrom, setOpenStartFrom] = useState(false);
  const [openStartTo, setOpenStartTo] = useState(false);
  const [openCompletionFrom, setOpenCompletionFrom] = useState(false);
  const [openCompletionTo, setOpenCompletionTo] = useState(false);

  const datePickerSlotProps = (onOpen: () => void) => ({
    textField: {
      size: "small" as const,
      variant: "outlined" as const,
      onClick: onOpen,
      InputProps: {
        readOnly: true,
        sx: {
          "& .MuiInputAdornment-root button": {
            border: "none",
            padding: 0,
            background: "transparent",
            "&:hover": { background: "transparent" },
          },
        },
      },
    },
    openPickerButton: { sx: { border: "none" } },
    openPickerIcon: { sx: { width: "15px" } },
  });

  return (
    <Drawer
      id="edit-training-records-filter-drawer"
      anchor="right"
      open={showFilter}
      onClose={() => setShowFilter(false)}
      slotProps={{ paper: { sx: { backgroundColor: "white" } } }}
    >
      <Box
        sx={{
          width: 320,
          py: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
          }}
        >
          <Typography variant="h6">Filters</Typography>
          <IconButton
            id="edit-training-records-filter-close-button"
            onClick={() => setShowFilter(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {filtersList.map((group, groupIndex) => (
            <Box key={group.title}>
              <Box sx={{ m: 1 }}>
                <Typography sx={{ mb: 1, fontWeight: 600 }}>
                  {group.title}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {group.items.map((item, itemIndex) => (
                    <FormControlLabel
                      key={item.id}
                      control={
                        <Checkbox
                          checked={!!item.checked}
                          onChange={() =>
                            handleCheckboxChange(groupIndex, itemIndex)
                          }
                          size="small"
                        />
                      }
                      label={item.name}
                    />
                  ))}
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
            </Box>
          ))}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ m: 1, mt: 2 }}>
              <Typography sx={{ mb: 1, fontWeight: 600 }}>
                Start Date
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <DatePicker
                  label="From"
                  value={startDateFrom}
                  onChange={(v) => onStartDateFromChange(v as Dayjs | null)}
                  format="DD-MMM-YYYY"
                  maxDate={startDateTo || undefined}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  open={openStartFrom}
                  onOpen={() => setOpenStartFrom(true)}
                  onClose={() => setOpenStartFrom(false)}
                  slotProps={datePickerSlotProps(() => setOpenStartFrom(true))}
                  sx={{ flex: 1, "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
                />
                <Typography sx={{ color: "#344054", fontWeight: 500 }}>
                  -
                </Typography>
                <DatePicker
                  label="To"
                  value={startDateTo}
                  onChange={(v) => onStartDateToChange(v as Dayjs | null)}
                  format="DD-MMM-YYYY"
                  minDate={startDateFrom || undefined}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  open={openStartTo}
                  onOpen={() => setOpenStartTo(true)}
                  onClose={() => setOpenStartTo(false)}
                  slotProps={datePickerSlotProps(() => setOpenStartTo(true))}
                  sx={{ flex: 1, "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
                />
              </Stack>
            </Box>

            <Box sx={{ m: 1, mt: 2 }}>
              <Typography sx={{ mb: 1, fontWeight: 600 }}>
                Completion Date
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <DatePicker
                  label="From"
                  value={completionDateFrom}
                  onChange={(v) => onCompletionDateFromChange(v as Dayjs | null)}
                  format="DD-MMM-YYYY"
                  maxDate={completionDateTo || undefined}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  open={openCompletionFrom}
                  onOpen={() => setOpenCompletionFrom(true)}
                  onClose={() => setOpenCompletionFrom(false)}
                  slotProps={datePickerSlotProps(() =>
                    setOpenCompletionFrom(true),
                  )}
                  sx={{ flex: 1, "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
                />
                <Typography sx={{ color: "#344054", fontWeight: 500 }}>
                  -
                </Typography>
                <DatePicker
                  label="To"
                  value={completionDateTo}
                  onChange={(v) => onCompletionDateToChange(v as Dayjs | null)}
                  format="DD-MMM-YYYY"
                  minDate={completionDateFrom || undefined}
                  slots={{ openPickerIcon: CalendarTodayOutlinedIcon }}
                  open={openCompletionTo}
                  onOpen={() => setOpenCompletionTo(true)}
                  onClose={() => setOpenCompletionTo(false)}
                  slotProps={datePickerSlotProps(() => setOpenCompletionTo(true))}
                  sx={{ flex: 1, "& .MuiSvgIcon-root": { fontSize: "1.5rem" } }}
                />
              </Stack>
            </Box>
          </LocalizationProvider>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Stack direction="row" spacing={1} sx={{ px: 1 }}>
          <Button
            sx={{
              px: 2,
              borderRadius: "12px",
              backgroundColor: `${brand[600]} !important`,
              color: "white",
              "&:hover": {
                bgcolor: `${brand[700]} !important`,
                textDecoration: "underline",
              },
            }}
            onClick={() => {
              saveFilters();
              setShowFilter(false);
            }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              clearFilters();
              setShowFilter(false);
            }}
          >
            Clear all
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default Filter;
